/**
 * Validation for the scheduling link.
 *
 * The field previously carried `rules={[{ required: true }]}` and nothing else,
 * so any non-empty string saved. This is the link Rio hands to candidates when
 * booking an interview, so "asdf" saved cleanly and the failure surfaced to a
 * candidate holding a dead link -- the person least able to do anything about
 * it, and the one nobody hears from.
 *
 * DELIBERATELY NOT antd's `type: 'url'`. That requires a scheme, so
 * "calendly.com/team/dana" is rejected, and that is exactly what someone
 * copying from their calendar app's UI ends up with. Rejecting a link the user
 * knows is correct teaches them the field is broken.
 *
 * DELIBERATELY NOT REWRITING what the user typed either. Silently storing
 * something other than what was entered is its own small dishonesty, and it
 * would change stored data on a reskin. The validator accepts a scheme-less
 * host, and the UI says which form gets sent. Whether to normalise on save is
 * a product decision, raised rather than taken.
 */

/** Result of checking a candidate scheduling link. */
export interface LinkCheck {
  ok: boolean;
  /** User-facing reason, present only when ok is false. */
  reason?: string;
  /** The absolute URL this would resolve to. Present only when ok. */
  resolved?: string;
}

/**
 * Hosts that are almost certainly a mistake rather than a scheduling page --
 * a local dev server or a loopback address cannot be reached by a candidate.
 */
const UNREACHABLE_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

export const checkSchedulingLink = (raw: string): LinkCheck => {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return {
      ok: false,
      reason: 'Add the scheduling link from your calendar app.',
    };
  }

  // Whitespace inside a URL is almost always a paste that picked up
  // surrounding text, and it produces a link that silently 404s.
  if (/\s/.test(trimmed)) {
    return {
      ok: false,
      reason:
        'That contains a space, so it will not open. Paste just the link.',
    };
  }

  // A scheme-like prefix with no "//" -- mailto:, javascript:, tel: -- must be
  // rejected outright rather than prefixed. Prefixing turns
  // "mailto:dana@avrio.com" into "https://mailto:dana@avrio.com", which parses
  // as username "mailto", password "dana", host "avrio.com" and sails through
  // every check below. Found by the test, not by reading the code.
  const schemeLike = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(trimmed);
  if (schemeLike && !/^https?$/i.test(schemeLike[1])) {
    return { ok: false, reason: 'Use a web link starting with https://' };
  }

  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return { ok: false, reason: 'That does not look like a link.' };
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return {
      ok: false,
      reason: 'Use a web link starting with https://',
    };
  }

  // Credentials in a scheduling link are either a paste accident or the
  // classic look-alike: https://calendly.com@evil.com reads as calendly and
  // resolves to evil.com. A candidate has no way to tell.
  if (url.username || url.password) {
    return {
      ok: false,
      reason:
        'That link contains a username before the @, which does not go where it looks like it goes.',
    };
  }

  const host = url.hostname.toLowerCase();

  if (UNREACHABLE_HOSTS.has(host)) {
    return {
      ok: false,
      reason:
        'That address only works on your own machine, so a candidate cannot open it.',
    };
  }

  // A bare word with no dot is a hostname nobody outside a private network can
  // resolve -- "calendar", "scheduling". The most common form of the original
  // bug, and the one `required: true` let straight through.
  if (!host.includes('.')) {
    return {
      ok: false,
      reason:
        'That is missing a domain. A scheduling link looks like yourcalendar.com/your-name.',
    };
  }

  return { ok: true, resolved: url.toString() };
};
